import { isEnvelope, type ApiFieldError } from "./envelope";

/**
 * Error thrown for every failed response. The backend answers with the company error envelope;
 * `code` is its stable `sub_code` (e.g. "identity.otp_wrong_code", "VALIDATION_ERROR") — branch on it, never on
 * the human-readable `message`.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  /** Invalid fields of a 422 (wire names). */
  readonly fieldErrors: ApiFieldError[];
  readonly retryAfterSeconds: number | undefined;

  constructor(
    status: number,
    error: {
      code?: string;
      message?: string;
      fieldErrors?: ApiFieldError[];
      retryAfterSeconds?: number;
    } = {},
  ) {
    super(error.message ?? `HTTP ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = error.code;
    this.fieldErrors = error.fieldErrors ?? [];
    this.retryAfterSeconds = error.retryAfterSeconds;
  }
}

/** The request never got an HTTP response (offline, DNS, timeout). */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super("Network request failed", { cause });
    this.name = "NetworkError";
  }
}

export function isApiError(error: unknown, code?: string): error is ApiError {
  return error instanceof ApiError && (code === undefined || error.code === code);
}

/** Builds the ApiError of an error envelope; `status` is the HTTP status of the response. */
export function errorFromBody(
  status: number,
  body: unknown,
  retryHeader?: string | null,
): ApiError {
  const error = isEnvelope(body) ? body.error : null;
  const details = error?.details;
  const retryFromBody =
    details && typeof details === "object" && "retry_after_seconds" in details
      ? Number(details.retry_after_seconds)
      : undefined;
  const retryFromHeader = Number(retryHeader);
  return new ApiError(error?.code ?? status, {
    code: error?.sub_code,
    message: error?.message,
    fieldErrors: Array.isArray(details) ? (details as ApiFieldError[]) : [],
    retryAfterSeconds:
      retryFromBody !== undefined && Number.isFinite(retryFromBody)
        ? retryFromBody
        : Number.isFinite(retryFromHeader) && retryFromHeader > 0
          ? retryFromHeader
          : undefined,
  });
}

export async function toApiError(response: Response): Promise<ApiError> {
  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    // body is not JSON (proxy error page, empty body) — keep the status only
  }
  return errorFromBody(response.status, body, response.headers.get("Retry-After"));
}

/** What kind of failure a page shows when its data did not load (see `ErrorState`). */
export type LoadErrorKind =
  | "network"
  | "timeout"
  | "unauthorized"
  | "forbidden"
  | "notFound"
  | "validation"
  | "rateLimited"
  | "server"
  | "unknown";

/** Classifies by error class and HTTP status only — specific `sub_code`s are handled by the feature. */
export function loadErrorKind(error: unknown): LoadErrorKind {
  if (error instanceof NetworkError) {
    return error.cause instanceof DOMException && error.cause.name === "TimeoutError"
      ? "timeout"
      : "network";
  }
  if (!(error instanceof ApiError)) return "unknown";
  if (error.status === 401) return "unauthorized";
  if (error.status === 403) return "forbidden";
  if (error.status === 404) return "notFound";
  if (error.status === 400 || error.status === 422) return "validation";
  if (error.status === 429) return "rateLimited";
  if (error.status >= 500) return "server";
  return "unknown";
}
